import { Button, Typography, Card, CardContent, CardActions } from "@mui/material";
import { motion } from "framer-motion";
import { 
  HiWrenchScrewdriver, 
  HiPlus, 
  HiEye, 
  HiUsers, 
  HiClock,
  HiSparkles
} from "react-icons/hi2";
import { useNavigate } from "react-router-dom";

const DealerHome = () => {
  const navigate = useNavigate();
  const handleButtonClick = (action) => () => {
    navigate(action);
  };

  const recentActivities = [
    { action: "New bike added", time: "2 hours ago", type: "bike" },
    { action: "Customer interested in Royal Enfield", time: "4 hours ago", type: "customer" },
    { action: "Workshop updated", time: "1 day ago", type: "workshop" },
    { action: "New customer inquiry", time: "2 days ago", type: "customer" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Modern Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center mb-4">
            <HiWrenchScrewdriver className="w-10 h-10 text-primary-600 mr-4" />
            <h1 className="text-5xl font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent">
              Dealer Dashboard
            </h1>
          </div>
          <p className="text-gray-600 text-xl max-w-3xl mx-auto">
            Manage your workshops, bikes, and customer relationships with ease
          </p>
        </motion.div>

        {/* Main Actions Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12"
        >
          {buttons.map((button, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1, duration: 0.6 }}
              className="group"
            >
              <Card 
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border-0 cursor-pointer"
                sx={{ 
                  borderRadius: "16px",
                  "&:hover": {
                    boxShadow: "0 25px 50px rgba(0,0,0,0.15)"
                  }
                }}
                onClick={handleButtonClick(button.action)}
              >
                <CardContent className="p-8 text-center">
                  <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-r ${button.color} flex items-center justify-center text-white shadow-lg`}>
                    {button.icon}
                  </div>
                  <Typography 
                    variant="h6" 
                    className="font-bold text-gray-900 group-hover:text-primary-600 transition-colors"
                    sx={{ fontSize: "1.25rem" }}
                  >
                    {button.label}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    className="text-gray-500 mt-2"
                    sx={{ fontSize: "0.9rem" }}
                  >
                    {button.description}
                  </Typography>
                </CardContent>
                <CardActions className="justify-center pb-6">
                  <Button
                    variant="contained"
                    className={`bg-gradient-to-r ${button.color} hover:opacity-90 text-white rounded-xl px-6 py-2 font-semibold transition-all duration-300 shadow-lg hover:shadow-xl`}
                    sx={{
                      borderRadius: "12px",
                      textTransform: "none",
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      px: 4,
                      py: 1
                    }}
                    startIcon={button.actionIcon}
                  >
                    {button.actionText}
                  </Button>
                </CardActions>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* Recent Activity Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
        >
          <Card 
            className="bg-white rounded-2xl shadow-lg border-0"
            sx={{ borderRadius: "16px" }}
          >
            <CardContent className="p-8">
              <div className="flex items-center mb-6">
                <HiClock className="w-6 h-6 text-primary-600 mr-3" />
                <Typography 
                  variant="h5" 
                  className="font-bold text-gray-900"
                  sx={{ fontSize: "1.5rem" }}
                >
                  Recent Activity
                </Typography>
              </div>
              
              <div className="space-y-4">
                {recentActivities.map((activity, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.7 + index * 0.1, duration: 0.6 }}
                    className="flex items-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    <div className={`w-3 h-3 rounded-full mr-4 ${
                      activity.type === 'bike' ? 'bg-green-500' :
                      activity.type === 'customer' ? 'bg-blue-500' :
                      'bg-purple-500'
                    }`}></div>
                    <div className="flex-1">
                      <Typography 
                        variant="body1" 
                        className="font-medium text-gray-900"
                        sx={{ fontSize: "1rem" }}
                      >
                        {activity.action}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        className="text-gray-500"
                        sx={{ fontSize: "0.85rem" }}
                      >
                        {activity.time}
                      </Typography>
                    </div>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

const buttons = [
  {
    label: "Add Workshop",
    description: "Create and manage your service centers",
    icon: <HiWrenchScrewdriver className="w-10 h-10" />,
    action: "/add-workshop",
    color: "from-orange-500 to-orange-600",
    actionIcon: <HiPlus className="w-4 h-4" />,
    actionText: "Add New"
  },
  {
    label: "Add Bike",
    description: "List your bikes for sale",
    icon: <HiSparkles className="w-10 h-10" />,
    action: "/sell-bike",
    color: "from-blue-500 to-blue-600",
    actionIcon: <HiPlus className="w-4 h-4" />,
    actionText: "List Bike"
  },
  {
    label: "Show Workshop",
    description: "View and manage your workshops",
    icon: <HiEye className="w-10 h-10" />,
    action: "/show-my-workshop",
    color: "from-green-500 to-green-600",
    actionIcon: <HiEye className="w-4 h-4" />,
    actionText: "View All"
  },
  {
    label: "Customers",
    description: "Manage interested customers",
    icon: <HiUsers className="w-10 h-10" />,
    action: "/interest-customers",
    color: "from-purple-500 to-purple-600",
    actionIcon: <HiUsers className="w-4 h-4" />,
    actionText: "View All"
  },
];

export default DealerHome;
